import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useReactToPrint } from 'react-to-print';
import { ArrowLeft, FileText, Plus, Trash2, Edit2, Printer } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(v || 0);
const typeLabels = {
  mecanica: 'Mecânica', pecas: 'Peças', legalizacao: 'Legalização',
  transporte: 'Transporte', limpeza: 'Limpeza', outros: 'Outros'
};
const formatDate = (d) => { if (!d) return '-'; const [y, m, day] = d.split('-'); return `${day}/${m}/${y}`; };

// Print component
const VehicleReportPrint = React.forwardRef(({ report }, ref) => {
  if (!report) return null;
  return (
    <div ref={ref} style={{ width: '210mm', padding: '15mm', background: 'white', color: 'black', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '5mm', borderBottom: '2px solid #DC2626', marginBottom: '8mm' }}>
        <div>
          <div style={{ fontSize: '20px', fontWeight: '800', color: '#DC2626' }}>ICARDETAIL</div>
          <div style={{ fontSize: '9px', color: '#666', letterSpacing: '2px' }}>RELATÓRIO FINANCEIRO</div>
        </div>
        <div style={{ textAlign: 'right', fontSize: '10px', color: '#666' }}>
          <div>Data: {new Date().toLocaleDateString('pt-PT')}</div>
        </div>
      </div>

      <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '3mm' }}>{report.brand} {report.model} ({report.year})</h2>
      <span style={{ fontSize: '11px', padding: '2px 10px', border: '1px solid #666', color: '#666' }}>{report.status}</span>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '5mm', marginTop: '8mm', marginBottom: '8mm' }}>
        <div style={{ background: '#f5f5f5', padding: '4mm' }}>
          <div style={{ fontSize: '9px', color: '#888' }}>PREÇO COMPRA</div>
          <div style={{ fontSize: '16px', fontWeight: '700' }}>{fmt(report.purchase_price)}</div>
        </div>
        <div style={{ background: '#f5f5f5', padding: '4mm' }}>
          <div style={{ fontSize: '9px', color: '#888' }}>DESPESAS</div>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#DC2626' }}>{fmt(report.total_expenses)}</div>
        </div>
        <div style={{ background: '#f5f5f5', padding: '4mm' }}>
          <div style={{ fontSize: '9px', color: '#888' }}>CUSTO TOTAL</div>
          <div style={{ fontSize: '16px', fontWeight: '700' }}>{fmt(report.total_cost)}</div>
        </div>
        <div style={{ background: '#f5f5f5', padding: '4mm' }}>
          <div style={{ fontSize: '9px', color: '#888' }}>LUCRO LÍQUIDO</div>
          <div style={{ fontSize: '16px', fontWeight: '700', color: report.net_profit >= 0 ? '#10B981' : '#DC2626' }}>{fmt(report.net_profit)} ({report.margin}%)</div>
        </div>
      </div>

      <div style={{ fontSize: '9px', color: '#888', marginBottom: '2mm' }}>PREÇO VENDA: {fmt(report.sale_price)}</div>

      {Object.keys(report.expense_breakdown || {}).length > 0 && (
        <div style={{ marginTop: '5mm', marginBottom: '5mm' }}>
          <div style={{ fontSize: '12px', fontWeight: '700', marginBottom: '3mm', borderBottom: '1px solid #ddd', paddingBottom: '2mm' }}>DESPESAS POR TIPO</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '3mm' }}>
            {Object.entries(report.expense_breakdown).map(([type, amount]) => (
              <div key={type} style={{ fontSize: '11px' }}>
                <span style={{ color: '#888' }}>{typeLabels[type] || type}: </span>
                <span style={{ fontWeight: '600' }}>{fmt(amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {report.expenses?.length > 0 && (
        <div style={{ marginTop: '5mm' }}>
          <div style={{ fontSize: '12px', fontWeight: '700', marginBottom: '3mm', borderBottom: '1px solid #ddd', paddingBottom: '2mm' }}>HISTÓRICO DE DESPESAS</div>
          <table style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #ddd' }}>
                <th style={{ textAlign: 'left', padding: '2mm', color: '#888' }}>Data</th>
                <th style={{ textAlign: 'left', padding: '2mm', color: '#888' }}>Tipo</th>
                <th style={{ textAlign: 'left', padding: '2mm', color: '#888' }}>Descrição</th>
                <th style={{ textAlign: 'right', padding: '2mm', color: '#888' }}>Valor</th>
              </tr>
            </thead>
            <tbody>
              {report.expenses.map((e, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '2mm' }}>{formatDate(e.date)}</td>
                  <td style={{ padding: '2mm' }}>{typeLabels[e.expense_type] || e.expense_type}</td>
                  <td style={{ padding: '2mm' }}>{e.description || '-'}</td>
                  <td style={{ textAlign: 'right', padding: '2mm', fontWeight: '600' }}>{fmt(e.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: '10mm', paddingTop: '3mm', borderTop: '1px solid #ddd', fontSize: '9px', color: '#888', display: 'flex', justifyContent: 'space-between' }}>
        <span>icardetail.pt | 919 714 805</span>
        <span>Documento interno - Uso confidencial</span>
      </div>
    </div>
  );
});
VehicleReportPrint.displayName = 'VehicleReportPrint';

const AdminVehicleFinance = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editPrices, setEditPrices] = useState(false);
  const [prices, setPrices] = useState({ purchase_price: '', sale_price: '' });
  const printRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchReport = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/finance/report/vehicle/${id}`, { headers });
      setReport(res.data);
      setPrices({ purchase_price: String(res.data.purchase_price || ''), sale_price: String(res.data.sale_price || '') });
    } catch (err) {
      toast.error('Erro ao carregar relatório');
      navigate('/admin/financas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const reactToPrintFn = useReactToPrint({ contentRef: printRef, documentTitle: report ? `Relatorio_${report.brand}_${report.model}` : 'Relatorio' });

  const savePrices = async () => {
    try {
      await axios.put(`${API_URL}/api/finance/vehicle/${id}`, {
        purchase_price: parseFloat(prices.purchase_price) || 0,
        sale_price: parseFloat(prices.sale_price) || 0
      }, { headers });
      toast.success('Preços atualizados');
      setEditPrices(false);
      fetchReport();
    } catch (err) {
      toast.error('Erro ao atualizar preços');
    }
  };

  const deleteExpense = async (expId) => {
    if (!window.confirm('Eliminar esta despesa?')) return;
    try {
      await axios.delete(`${API_URL}/api/finance/expenses/${expId}`, { headers });
      toast.success('Despesa eliminada');
      fetchReport();
    } catch (err) {
      toast.error('Erro');
    }
  };

  if (loading) return <AdminLayout><div className="text-center py-20"><p className="text-zinc-400">A carregar...</p></div></AdminLayout>;
  if (!report) return <AdminLayout><p className="text-zinc-400">Relatório não encontrado</p></AdminLayout>;

  return (
    <AdminLayout>
      <div className="animate-fade-in">
        {/* Hidden print */}
        <div style={{ position: 'absolute', left: '-9999px', top: 0 }}><VehicleReportPrint ref={printRef} report={report} /></div>

        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button onClick={() => navigate('/admin/financas')} className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors" data-testid="back-to-finance">
            <ArrowLeft size={24} />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-medium text-white" data-testid="vehicle-report-title">
              {report.brand} {report.model} <span className="text-zinc-500">({report.year})</span>
            </h1>
            <p className="text-zinc-500">Relatório financeiro detalhado</p>
          </div>
          <Button onClick={() => reactToPrintFn()} className="bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700" data-testid="print-report-btn">
            <Printer size={18} className="mr-2" /> Exportar PDF
          </Button>
        </div>

        {/* KPI Row */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8" data-testid="vehicle-kpis">
          <div className="bg-[#12141D] border border-zinc-800 p-5">
            <p className="text-zinc-500 text-xs mb-1">Preço Compra</p>
            <p className="text-white text-xl font-semibold">{report.purchase_price > 0 ? fmt(report.purchase_price) : '-'}</p>
          </div>
          <div className="bg-[#12141D] border border-zinc-800 p-5">
            <p className="text-zinc-500 text-xs mb-1">Despesas</p>
            <p className="text-orange-400 text-xl font-semibold">{fmt(report.total_expenses)}</p>
          </div>
          <div className="bg-[#12141D] border border-zinc-800 p-5">
            <p className="text-zinc-500 text-xs mb-1">Custo Total</p>
            <p className="text-white text-xl font-semibold">{fmt(report.total_cost)}</p>
          </div>
          <div className="bg-[#12141D] border border-zinc-800 p-5">
            <p className="text-zinc-500 text-xs mb-1">Preço Venda</p>
            <p className="text-white text-xl font-semibold">{fmt(report.sale_price)}</p>
          </div>
          <div className={`bg-[#12141D] border p-5 ${report.net_profit >= 0 ? 'border-emerald-600/30' : 'border-red-600/30'}`}>
            <p className="text-zinc-500 text-xs mb-1">Lucro Líquido</p>
            <p className={`text-xl font-semibold ${report.net_profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{fmt(report.net_profit)}</p>
            <p className={`text-xs ${report.margin >= 10 ? 'text-emerald-500' : report.margin >= 0 ? 'text-yellow-500' : 'text-red-500'}`}>{report.margin}% margem</p>
          </div>
        </div>

        {/* Edit Prices */}
        <div className="bg-[#12141D] border border-zinc-800 p-5 mb-6" data-testid="edit-prices-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-medium">Preços de Compra / Venda</h3>
            {!editPrices && (
              <Button size="sm" onClick={() => setEditPrices(true)} className="bg-zinc-800 text-white hover:bg-zinc-700 text-xs" data-testid="edit-prices-btn">
                <Edit2 size={14} className="mr-1" /> Editar
              </Button>
            )}
          </div>
          {editPrices ? (
            <div className="flex items-end gap-4">
              <div className="flex-1">
                <label className="text-zinc-400 text-sm mb-1 block">Preço de Compra (EUR)</label>
                <Input type="number" value={prices.purchase_price} onChange={e => setPrices(p => ({ ...p, purchase_price: e.target.value }))}
                  className="bg-[#090A0F] border-zinc-800 text-white" data-testid="purchase-price-input" />
              </div>
              <div className="flex-1">
                <label className="text-zinc-400 text-sm mb-1 block">Preço de Venda (EUR)</label>
                <Input type="number" value={prices.sale_price} onChange={e => setPrices(p => ({ ...p, sale_price: e.target.value }))}
                  className="bg-[#090A0F] border-zinc-800 text-white" data-testid="sale-price-input" />
              </div>
              <Button onClick={savePrices} className="bg-red-600 text-white hover:bg-red-700" data-testid="save-prices-btn">Guardar</Button>
              <Button onClick={() => setEditPrices(false)} className="bg-zinc-800 text-white hover:bg-zinc-700">Cancelar</Button>
            </div>
          ) : (
            <div className="flex gap-8 text-sm">
              <div><span className="text-zinc-500">Compra:</span> <span className="text-white font-medium">{report.purchase_price > 0 ? fmt(report.purchase_price) : 'Não definido'}</span></div>
              <div><span className="text-zinc-500">Venda:</span> <span className="text-white font-medium">{fmt(report.sale_price)}</span></div>
            </div>
          )}
        </div>

        {/* Expense Breakdown */}
        {Object.keys(report.expense_breakdown || {}).length > 0 && (
          <div className="bg-[#12141D] border border-zinc-800 p-5 mb-6" data-testid="expense-breakdown">
            <h3 className="text-white font-medium mb-4">Despesas por Tipo</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {Object.entries(report.expense_breakdown).map(([type, amount]) => (
                <div key={type} className="bg-[#090A0F] border border-zinc-800 p-3">
                  <p className="text-zinc-500 text-xs uppercase">{typeLabels[type] || type}</p>
                  <p className="text-red-400 font-semibold">{fmt(amount)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Expenses List */}
        <div className="bg-[#12141D] border border-zinc-800 overflow-hidden" data-testid="vehicle-expenses-list">
          <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
            <h3 className="text-white font-medium">Histórico de Despesas</h3>
            <Link to={`/admin/financas/despesas`}>
              <Button size="sm" className="bg-red-600 text-white hover:bg-red-700 text-xs" data-testid="add-vehicle-expense-btn">
                <Plus size={14} className="mr-1" /> Adicionar
              </Button>
            </Link>
          </div>
          {report.expenses?.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">Nenhuma despesa registada para esta viatura</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-800 text-zinc-500">
                  <th className="text-left px-5 py-3 font-medium">Data</th>
                  <th className="text-left px-4 py-3 font-medium">Tipo</th>
                  <th className="text-left px-4 py-3 font-medium">Descrição</th>
                  <th className="text-right px-4 py-3 font-medium">Valor</th>
                  <th className="text-center px-4 py-3 font-medium">Ação</th>
                </tr>
              </thead>
              <tbody>
                {report.expenses.map(e => (
                  <tr key={e.id} className="border-b border-zinc-800/50 hover:bg-white/[0.02]">
                    <td className="px-5 py-3 text-zinc-300">{formatDate(e.date)}</td>
                    <td className="px-4 py-3 text-zinc-300">{typeLabels[e.expense_type] || e.expense_type}</td>
                    <td className="px-4 py-3 text-zinc-400">{e.description || '-'}</td>
                    <td className="text-right px-4 py-3 text-white font-medium">{fmt(e.amount)}</td>
                    <td className="text-center px-4 py-3">
                      <button onClick={() => deleteExpense(e.id)} className="text-zinc-400 hover:text-red-500" data-testid={`delete-vexp-${e.id}`}>
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminVehicleFinance;
